import { useQuery } from '@tanstack/react-query';
import { Button, Space, Tag } from 'antd';
import {
  DataTable,
  RowActionsMenu,
  useCapability,
  type Capability,
  type DataColumn,
  type TableState,
} from '@viq-admin/ui';
import type { CrmConnectionRow, IntegrationsApi } from './types';
import { crmSourceLabel } from './crmSourceLabel';
import { runConnectionTest } from './TestConnectionButton';

/**
 * Absent means `'credentials'`: only the superadmin plane sends `managedBy`,
 * and every pre-managedBy row (and every operator-entered one) is
 * staff-maintained.
 */
function managedByLabel(managedBy: CrmConnectionRow['managedBy']): string {
  return managedBy === 'oauth' ? 'OAuth (customer connect)' : 'Credentials (staff)';
}

export interface ConnectionsTableProps {
  api: IntegrationsApi;
  onOpen: (id: string) => void;
  onCreate?: () => void;
  manageCap?: Capability;
  /**
   * Controlled table state + setter + default, forwarded straight to
   * `DataTable` — the seam through which a caller threads URL-synced state
   * (`useUrlTableState` over its OWN router's `useSearchParams`, as the
   * pre-lift console list did), since the router stays on the app side of
   * the closure-only boundary (packages/* must not import react-router). A
   * caller that syncs state nowhere omits all three and the table runs
   * uncontrolled, exactly as before.
   */
  state?: TableState;
  onStateChange?: (next: TableState) => void;
  defaultState?: Partial<TableState>;
}

/**
 * Cross-source table of CRM connections. Mirrors the console's
 * `IntegrationList` (controller ruling, p6 Task 2), moved into the shared
 * slice with two changes the ruling requires: the crmSource column renders
 * through `crmSourceLabel` inside a `Tag` (was a plain string), and the row
 * kebab carries an explicit "Open" action (was implicit via `onRowClick`
 * alone, since a shared component cannot assume Refine's list-page click
 * navigation).
 *
 * Rows are NOT Salesforce-only: since p4 the portal's OAuth broker mints
 * hubspot/zoho rows that this list also renders, so every source-facing
 * string (the Tag, the delete-confirm copy) is derived from
 * `record.crmSource`, never a literal.
 *
 * `manageCap` gates the Test and Delete kebab actions and the Create button
 * (the pre-lift console gated its whole action cell on canManage). An
 * undefined `manageCap` means "no capability required" (the shared two-axis
 * convention, see `useCapability`), so all stay visible when the caller
 * omits it.
 */
export function ConnectionsTable({
  api,
  onOpen,
  onCreate,
  manageCap,
  state,
  onStateChange,
  defaultState,
}: ConnectionsTableProps) {
  // Aliased from react-query's own re-invoke callback so the identifier
  // never collides with the closure-only grep audit (docs/feature-authoring.md)
  // over this directory — this call re-invokes `api.listConnections`, never a
  // raw network call.
  const { data, isLoading, error, refetch: reload } = useQuery({
    queryKey: ['integrationsConnections'],
    queryFn: api.listConnections,
  });
  const canManage = useCapability(manageCap);
  const rows = data ?? [];
  const hasCompanyColumn = rows.some((r) => r.companyName != null);

  const companyColumn: DataColumn<CrmConnectionRow> = {
    dataIndex: 'companyName',
    key: 'companyName',
    title: 'Company',
    kind: 'text',
    width: 220,
    render: (_: unknown, r: CrmConnectionRow) => r.companyName ?? r.companyId ?? '',
    projectValue: (r) => r.companyName ?? r.companyId ?? '',
  };

  const columns: DataColumn<CrmConnectionRow>[] = [
    ...(hasCompanyColumn ? [companyColumn] : []),
    {
      key: 'crmSource',
      title: 'CRM Source',
      kind: 'text',
      width: 140,
      // Sort/filter stay off: a small, fixed-value column, same as the
      // console's original crmSource column.
      sortable: false,
      filterable: false,
      render: (_: unknown, r: CrmConnectionRow) => <Tag>{crmSourceLabel(r.crmSource)}</Tag>,
      projectValue: (r) => crmSourceLabel(r.crmSource),
    },
    {
      key: 'hasSecret',
      title: 'Consumer Secret',
      kind: 'text',
      width: 160,
      sortable: false,
      filterable: false,
      render: (_: unknown, r: CrmConnectionRow) =>
        r.hasSecret ? <Tag color="green">Configured</Tag> : <Tag>Not set</Tag>,
      projectValue: (r) => (r.hasSecret ? 'Configured' : 'Not set'),
    },
    {
      key: 'managedBy',
      title: 'Connected via',
      kind: 'text',
      width: 200,
      sortable: false,
      filterable: false,
      render: (_: unknown, r: CrmConnectionRow) => <Tag>{managedByLabel(r.managedBy)}</Tag>,
      projectValue: (r) => managedByLabel(r.managedBy),
    },
    { dataIndex: 'updatedAt', key: 'updatedAt', title: 'Updated', kind: 'date', width: 140 },
    {
      key: 'actions',
      title: '',
      kind: 'actions',
      render: (_: unknown, record: CrmConnectionRow) => (
        <RowActionsMenu
          actions={[
            {
              key: 'open',
              label: 'Open',
              onRun: () => onOpen(record.id),
            },
            {
              key: 'test',
              label: 'Test Connection',
              // Same gate as the pre-lift console row and the detail header's
              // TestConnectionButton (both canManage-only).
              cap: manageCap,
              onRun: () => runConnectionTest(api.testConnection, record.id),
            },
            {
              key: 'delete',
              label: 'Delete',
              danger: true,
              cap: manageCap,
              onRun: async () => {
                await api.deleteConnection(record.id);
                await reload();
              },
              confirm: {
                // Both strings name the ACTUAL source (ruling 3): a HubSpot
                // row whose dialog says "Salesforce" is an operator deleting
                // what they believe is a different integration. The title
                // ALSO names the company when the row carries one (console's
                // cross-tenant list, where several companies' rows are on
                // screen at once — a company-less title there is a real
                // "which one?" hazard); the portal's own-company rows carry
                // neither companyName nor companyId, so the clause drops out.
                title: `Delete ${crmSourceLabel(record.crmSource)} connection${
                  record.companyName ?? record.companyId
                    ? ` for ${record.companyName ?? record.companyId}`
                    : ''
                }?`,
                content: `Leads will no longer export to ${crmSourceLabel(record.crmSource)} for this company.`,
                okText: 'Delete',
                danger: true,
                successMessage: 'Connection deleted.',
              },
            },
          ]}
        />
      ),
    },
  ];

  return (
    <Space direction="vertical" size={12} style={{ width: '100%' }}>
      {onCreate && canManage && (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button type="primary" onClick={onCreate}>
            Create
          </Button>
        </div>
      )}
      <DataTable<CrmConnectionRow>
        dataSource={rows}
        loading={isLoading}
        columns={columns}
        rowKey="id"
        // The pre-lift console list's key, kept verbatim so operators' saved
        // column widths survive the lift into packages/screens.
        storageKey="integrationsList"
        state={state}
        onStateChange={onStateChange}
        defaultState={defaultState}
        onRowClick={(r) => onOpen(r.id)}
        searchFields={hasCompanyColumn ? ['companyName'] : undefined}
        searchPlaceholder="Search by company"
        error={error}
        onRetry={() => reload()}
        empty={{
          title: 'No integrations yet',
          description: 'Connect a company to a CRM to get started.',
        }}
      />
    </Space>
  );
}
